<template>
	<div class="paper-characterization-editor">
		<div v-for="axis in axes" :key="axis.key" class="mb-4">
			<div class="text-body-2 font-weight-medium mb-1">
				{{ axis.label }}
			</div>

			<!-- Assigned concepts. A confirmed (manual / auto) tag is a removable orange
			     (DACTYL) chip; an AI-suggested tag is a distinct pending chip that must be
			     confirmed or rejected before it counts (DCTL-2157). -->
			<div class="d-flex flex-wrap align-center ga-1 mb-2">
				<template
					v-for="concept in groups[axis.key]"
					:key="concept.dactylConceptRowId"
				>
					<!-- Pending AI suggestion: lighter/dashed, NOT closable, with inline
					     confirm (orange) + reject (white/blue) icon buttons. -->
					<v-chip
						v-if="concept.origin === 'ai_suggested'"
						color="amber-darken-2"
						variant="text"
						size="small"
						class="pending-chip"
						:aria-label="`Pending suggestion ${concept.preferredName}`"
					>
						<v-icon start size="20">mdi-robot-outline</v-icon>
						{{ concept.preferredName }}
						<span class="text-fg-2 ml-1">
							{{ concept.dactylConceptId }}
						</span>
						<v-btn
							icon="mdi-check"
							color="secondary"
							variant="flat"
							size="x-small"
							class="ml-1"
							:disabled="busy"
							:aria-label="`Confirm ${concept.preferredName}`"
							@click="confirmConcept(axis.key, concept.dactylConceptRowId)"
						/>
						<v-btn
							icon="mdi-close"
							color="primary"
							variant="text"
							size="x-small"
							class="ml-1"
							:disabled="busy"
							:aria-label="`Reject ${concept.preferredName}`"
							@click="rejectConcept(axis.key, concept.dactylConceptRowId)"
						/>
					</v-chip>

					<!-- Confirmed (manual / auto): the existing removable orange chip. -->
					<v-chip
						v-else
						closable
						color="amber-darken-2"
						variant="tonal"
						size="small"
						:disabled="busy"
						:aria-label="`Remove ${concept.preferredName}`"
						@click:close="removeConcept(axis.key, concept.dactylConceptRowId)"
					>
						<v-icon start size="20">mdi-qrcode</v-icon>
						{{ concept.preferredName }}
						<span class="text-fg-2 ml-1">
							{{ concept.dactylConceptId }}
						</span>
					</v-chip>
				</template>
				<span
					v-if="!groups[axis.key].length"
					class="text-caption text-grey-darken-1"
				>
					None assigned
				</span>
			</div>

			<!-- Add: autocomplete over this axis's characterization concepts,
			     searchable by name / DACTYL id / NCI id (mirrors the browser's
			     conceptsOfType filter). Selecting a concept assigns it. -->
			<v-autocomplete
				v-model="pending[axis.key]"
				:items="availableOptions(axis.key)"
				item-title="preferredName"
				item-value="id"
				:custom-filter="filterConcept"
				:label="`Add ${axis.label.toLowerCase()}`"
				:loading="optionsLoading"
				:disabled="busy"
				density="compact"
				variant="outlined"
				prepend-inner-icon="mdi-plus"
				hide-details
				clearable
				no-data-text="No matching concepts"
				@update:model-value="(id) => onSelect(axis.key, id)"
			>
				<template #item="{ props, item }">
					<v-list-item v-bind="props" :title="item.raw.preferredName">
						<template #append>
							<v-chip
								size="x-small"
								color="amber-darken-2"
								variant="tonal"
								class="ml-2"
							>
								{{ item.raw.dactylConceptId }}
							</v-chip>
						</template>
					</v-list-item>
				</template>
			</v-autocomplete>
		</div>
	</div>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import {
	CHARACTERIZATION_AXES,
	axisLabel,
	axisGroups,
	emptyAxisArrays,
	emptyAxisNulls,
} from "@/constants/characterizationAxes";

/**
 * PaperCharacterizationEditor - the paper-side therapeutic-area assignment UI
 * (DCTL-2093, Phase 5c.3). Per characterization axis it shows the
 * paper's assigned characterization concepts as removable orange DACTYL chips plus an
 * autocomplete to add more, searching that axis's characterization concepts by name /
 * DACTYL id / NCI id. Add/remove persist immediately through the
 * /paper-characterizations endpoints, independent of any parent form's Save button.
 *
 * Normalization is deliberately NOT an axis here - it holds scope, it is not a thing a
 * paper is "about". The characterization axes are every concept type except normalization.
 */
export default {
	name: "PaperCharacterizationEditor",

	props: {
		paperUuid: { type: String, required: true },
	},

	data() {
		return {
			axes: CHARACTERIZATION_AXES.map((key) => ({
				key,
				label: axisLabel(key),
			})),
			// The paper's assigned characterizations, grouped by axis (the shape every
			// endpoint returns).
			groups: emptyAxisArrays(),
			// All characterization concepts per axis (the autocomplete options).
			options: emptyAxisArrays(),
			// Per-axis autocomplete selection (reset to null right after an add).
			pending: emptyAxisNulls(),
			optionsLoading: false,
			busy: false,
		};
	},

	watch: {
		paperUuid() {
			this.fetchCharacterizations();
		},
	},

	mounted() {
		this.fetchOptions();
		this.fetchCharacterizations();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		// Concepts of an axis not already assigned (so the autocomplete never offers a
		// duplicate).
		availableOptions(axisKey) {
			const assigned = new Set(
				this.groups[axisKey].map((c) => c.dactylConceptRowId),
			);
			return this.options[axisKey].filter((c) => !assigned.has(c.id));
		},

		// Match on preferred name, DACTYL id, and any NCI id - so a pasted id like
		// "C26747" or "DACTYL377" finds its concept, mirroring the browser's
		// conceptsOfType filter.
		filterConcept(_value, query, item) {
			const q = (query || "").trim().toLowerCase();
			if (!q) return true;
			const concept = item?.raw ?? {};
			const haystack = [
				concept.preferredName,
				concept.dactylConceptId,
				...(concept.nciConceptIds || []),
			];
			return haystack.some(
				(value) => value && String(value).toLowerCase().includes(q),
			);
		},

		onSelect(axisKey, conceptId) {
			if (conceptId == null) return;
			this.addConcept(axisKey, conceptId);
			// Clear the field so it is ready for the next add (one-shot picker).
			this.$nextTick(() => {
				this.pending[axisKey] = null;
			});
		},

		fetchCharacterizations() {
			if (!this.paperUuid) return;
			this.$http
				.post("/paper-characterizations/list", {
					paperUuid: this.paperUuid,
				})
				.then(({ data: responseData }) => {
					this.groups = this.normalizeGroups(responseData.data);
				})
				.catch((error) => {
					// This is the sole source for the assigned chips; a silent failure
					// would masquerade as "no characterizations". Surface it.
					console.error("Failed to load paper characterizations:", error);
					this.showNotification({
						status: "error",
						message: "Failed to load paper characterizations",
					});
				});
		},

		fetchOptions() {
			this.optionsLoading = true;
			Promise.all(
				this.axes.map((axis) =>
					this.$http
						.post("/concepts/list", { type: axis.key })
						.then(({ data: responseData }) => {
							this.options[axis.key] = responseData.data || [];
						}),
				),
			)
				.catch((error) => {
					console.error("Failed to load characterization concepts:", error);
					this.showNotification({
						status: "error",
						message: "Failed to load characterization concepts",
					});
				})
				.finally(() => {
					this.optionsLoading = false;
				});
		},

		addConcept(axisKey, conceptId) {
			this.busy = true;
			this.$http
				.post("/paper-characterizations/add", {
					paperUuid: this.paperUuid,
					dactylConceptRowId: conceptId,
				})
				.then(({ data: responseData }) => {
					this.groups = this.normalizeGroups(responseData.data);
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while adding the characterization";
					this.showNotification({ status: "error", message });
				})
				.finally(() => {
					this.busy = false;
				});
		},

		removeConcept(axisKey, conceptId) {
			this.busy = true;
			this.$http
				.post("/paper-characterizations/remove", {
					paperUuid: this.paperUuid,
					dactylConceptRowId: conceptId,
				})
				.then(({ data: responseData }) => {
					this.groups = this.normalizeGroups(responseData.data);
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while removing the characterization";
					this.showNotification({ status: "error", message });
				})
				.finally(() => {
					this.busy = false;
				});
		},

		// Confirm a pending AI suggestion: reuse the existing /add path, which flips the
		// active ai_suggested row's origin to "manual" (the trusted origin the axisScope
		// gate reads). Same grouped response as add/remove.
		confirmConcept(axisKey, conceptId) {
			this.addConcept(axisKey, conceptId);
		},

		// Reject a pending AI suggestion: soft-delete it AND stamp origin "rejected" (a
		// durable tombstone a backfill re-run will not revive). Same grouped response.
		rejectConcept(axisKey, conceptId) {
			this.busy = true;
			this.$http
				.post("/paper-characterizations/reject", {
					paperUuid: this.paperUuid,
					dactylConceptRowId: conceptId,
				})
				.then(({ data: responseData }) => {
					this.groups = this.normalizeGroups(responseData.data);
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while rejecting the characterization";
					this.showNotification({ status: "error", message });
				})
				.finally(() => {
					this.busy = false;
				});
		},

		// Defensive shape guard: always keep every axis key present as an array so the
		// template never indexes undefined (e.g. if the payload ever omits an axis).
		// Delegates to the shared, unit-tested axisGroups so a new axis is never dropped.
		normalizeGroups(data) {
			return axisGroups(data);
		},
	},
};
</script>

<style scoped>
/* Pending AI-suggestion chip: a dashed outline signals "not yet confirmed" without the
   solid tonal fill a confirmed tag gets. Vuetify has no dashed-border chip variant, so
   this is one of the few spots a custom rule is warranted over a utility class. */
.pending-chip {
	/* amber-darken-2 (#ff8f00) - matches the confirmed chip's color for continuity. */
	border: 1px dashed #ff8f00;
}
</style>
